import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Check, Loader2, X } from "lucide-react";

export default function BillingSuccess() {
  const loc = useLocation();
  const nav = useNavigate();
  const { refreshProfile } = useAuth();
  const [status, setStatus] = useState("checking"); // checking | paid | expired | failed | pending
  const [info, setInfo] = useState(null);

  useEffect(() => {
    const sid = new URLSearchParams(loc.search).get("session_id");
    if (!sid) {
      setStatus("failed");
      return;
    }
    let attempts = 0;
    let cancelled = false;
    const poll = async () => {
      attempts += 1;
      try {
        const { data } = await api.get(`/billing/status/${sid}`);
        if (data.payment_status === "paid") {
          setInfo(data);
          setStatus("paid");
          refreshProfile();
          return;
        }
        if (data.status === "expired") {
          setStatus("expired");
          return;
        }
        if (data.payment_status === "failed") {
          setStatus("failed");
          return;
        }
      } catch {
        if (attempts >= 8) {
          setStatus("failed");
          return;
        }
      }
      if (cancelled) return;
      if (attempts < 8) {
        setTimeout(poll, 2000);
      } else {
        setStatus("pending");
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [loc.search, refreshProfile]);

  return (
    <div className="min-h-screen bg-newsprint flex items-center justify-center px-6" data-testid="billing-success-page">
      <div className="max-w-lg w-full bg-white border border-gray-300 p-10 fade-up shadow-[6px_6px_0_0_rgba(0,47,167,0.12)]">
        {status === "checking" && (
          <div data-testid="billing-status-checking">
            <Loader2 size={28} className="animate-spin text-[#002FA7]" />
            <div className="font-mono text-xs uppercase tracking-[0.25em] text-gray-500 mt-6">
              Confirming payment
            </div>
            <h1 className="font-serif text-3xl mt-2 tracking-tight">
              Verifying your subscription…
            </h1>
          </div>
        )}

        {status === "paid" && (
          <div data-testid="billing-status-paid">
            <div className="inline-flex items-center justify-center w-12 h-12 bg-[#002FA7] text-white">
              <Check size={22} strokeWidth={2} />
            </div>
            <div className="font-mono text-xs uppercase tracking-[0.25em] text-[#002FA7] mt-6">
              {info?.plan?.toUpperCase()} · {info?.interval?.toUpperCase()}
            </div>
            <h1 className="font-serif text-4xl mt-2 tracking-tight leading-none">
              You&apos;re in.
            </h1>
            <p className="text-sm text-gray-700 leading-relaxed mt-4">
              Your plan is active. Daily Brief, Weekly Calendar, and your new generation limits
              are unlocked right now.
            </p>
            <button
              data-testid="billing-open-app"
              onClick={() => nav("/app")}
              className="mt-8 bg-[#002FA7] text-white hover:bg-[#00227A] px-6 py-3 font-medium"
            >
              Open Fount
            </button>
          </div>
        )}

        {(status === "expired" || status === "failed") && (
          <div data-testid="billing-status-failed">
            <div className="inline-flex items-center justify-center w-12 h-12 bg-red-600 text-white">
              <X size={22} strokeWidth={2} />
            </div>
            <h1 className="font-serif text-3xl mt-6 tracking-tight">
              Checkout didn&apos;t complete.
            </h1>
            <p className="text-sm text-gray-700 leading-relaxed mt-3">
              Your card wasn&apos;t charged. You can try again any time.
            </p>
            <div className="mt-6 flex items-center gap-3">
              <button
                onClick={() => nav("/pricing")}
                className="bg-[#002FA7] text-white hover:bg-[#00227A] px-5 py-2.5 font-medium text-sm"
              >
                Back to pricing
              </button>
              <button
                onClick={() => nav("/app")}
                className="border border-gray-300 hover:border-[#0a0a0a] px-5 py-2.5 font-medium text-sm"
              >
                Open app on Free
              </button>
            </div>
          </div>
        )}

        {status === "pending" && (
          <div data-testid="billing-status-pending">
            <div className="font-mono text-xs uppercase tracking-[0.25em] text-gray-500">
              Still processing
            </div>
            <h1 className="font-serif text-3xl mt-2 tracking-tight">
              Payment is taking a moment.
            </h1>
            <p className="text-sm text-gray-700 leading-relaxed mt-3">
              You&apos;ll receive an email when it clears. Refresh this page or open the app and
              we&apos;ll update your plan as soon as Stripe confirms.
            </p>
            <button
              onClick={() => nav("/app")}
              className="mt-6 bg-[#002FA7] text-white hover:bg-[#00227A] px-5 py-2.5 font-medium text-sm"
            >
              Open app
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
